import React from 'react'
import { Modal } from 'antd'
import uuidv4 from 'uuidv4'

import { Permission, Group } from './PermissionsTypes'
import ModalStyle from './PermissionsStyle'
import PermissionsControls from './PermissionsControls'
import PermissionsDefineGroup from './PermissionsDefineGroup'
import PermissionsTopImage from './PermissionsTopImage'
import PermissionService from '../../services/graphQL/graphQLPermission'
import I18n from '../../i18n';
import NotificationComponent from '@src/helpers/_showNotifications';

type PropsType = {
  visible: boolean,
  onToggleModal: any,
  currentGroup: Group,
  onLoadGroups: any
}

type StateType = {
  showControls: Boolean,
  permissions: Array<Permission>,
  groups: Array<Group>,
  currentGroup: Group,
  selectedPermission: Number | String | null,
  selectedGroup: Number | String | null,
  canGoControls: boolean,
  loadingPermissions: boolean,
  permissionsLoaded: boolean,
  finalPermissions: any
  savingPermissions: any,
  currentPermissions: any,
  gridKey: any
}

const permissionService = new PermissionService()

/**
 * Permissions modal manager.
 */
class PermissionsModal extends React.Component<PropsType, StateType> {
  private DEFAULT_PERMISSION_VALUE = 1
  private memoizedGroupName = ''

  constructor (props: PropsType) {
    super(props)

    this.state = {
      showControls: false,
      permissions: [],
      groups: [],
      currentGroup: this.props.currentGroup,
      selectedPermission: null,
      selectedGroup: null,
      canGoControls: true,
      loadingPermissions: true,
      permissionsLoaded: false,
      finalPermissions: [],
      savingPermissions: false,
      currentPermissions: null,
      gridKey: uuidv4()
    }

    this.createInitialPermissionsObject = this.createInitialPermissionsObject.bind(this)
    this.handleChangePermission = this.handleChangePermission.bind(this)
    this.handleDefineGroupName = this.handleDefineGroupName.bind(this)
    this.handleToggleControls = this.handleToggleControls.bind(this)
    this.loadPermissions = this.loadPermissions.bind(this)
    this.savePermissions = this.savePermissions.bind(this)
    this.getCurrentPermissionValue = this.getCurrentPermissionValue.bind(this)
    this.handleChangeAllPermissions = this.handleChangeAllPermissions.bind(this)
    this.handleCancelAction = this.handleCancelAction.bind(this)
  }

  componentDidMount () {
    this.memoizedGroupName = this.props.currentGroup.name
  }

  getCurrentPermissionValue(permissionId: any, groupId: any) {
    const { currentPermissions } = this.state

    if (!currentPermissions || !groupId) return this.DEFAULT_PERMISSION_VALUE

    const p = currentPermissions.permissions
    const setted = p.find((r: any) => r.target_group_id === groupId && r.group_permission_id === permissionId)

    return setted ? setted.rule : this.DEFAULT_PERMISSION_VALUE
  }

  createPermissionPiece(permissionId: any, groupId: any = null) {
    return {
      group_permission_id: permissionId,
      rule: this.getCurrentPermissionValue(permissionId, groupId),
      target_group_id: groupId
    }
  }

  createInitialPermissionsObject() {
    const { permissions, currentGroup, groups } = this.state

    const availableGroups = currentGroup.id ? groups.filter(r => r.id !== currentGroup.id) : groups
    const currentGroupPermissions: any = permissions.map((r: any) => this.createPermissionPiece(r.id, currentGroup.id))
    const otherGroupsPermissions: any = availableGroups.map((r: any) => permissions.map(p => this.createPermissionPiece(p.id, r.id))).values()
    const finalPermissions = {
      id: currentGroup.id,
      name: currentGroup.name,
      permissions: [...currentGroupPermissions]
    }

    for (let a of otherGroupsPermissions) {
      finalPermissions.permissions = [...finalPermissions.permissions, ...a]
    }

    this.setState({ finalPermissions: finalPermissions, currentPermissions: finalPermissions })
  }

  handleChangePermission (permissionId: any, rule: any, targetGroupId: any) {
    const { finalPermissions } = this.state
    const updatedPermissions = this.state.finalPermissions.permissions.map((r: any) => {
      r.rule = (r.group_permission_id === permissionId && r.target_group_id === targetGroupId) ? rule : r.rule
      return r
    })
    finalPermissions.permissions = updatedPermissions

    this.setState({ finalPermissions: finalPermissions })
  }

  handleChangeAllPermissions (rule: any){
    this.DEFAULT_PERMISSION_VALUE = rule
    const { currentPermissions } = this.state
    const updatedPermissions = currentPermissions.permissions.map((r: any) => {
      const permission = r
      permission.rule = rule
      return permission
    })

    currentPermissions.permissions = updatedPermissions
    this.setState({ currentPermissions, gridKey: uuidv4() })
  }

  handleDefineGroupName (e: any) {
    const group: Group = Object.assign(this.state.currentGroup, { name: e.target.value })
    const isEmpty = e.target.value.length === 0

    this.setState({ currentGroup: group, canGoControls: !isEmpty })
  }

  handleToggleControls (verifyCanGo: boolean = false): void|boolean {
    if (verifyCanGo) {
      const { currentGroup } = this.state
      const isEmpty = currentGroup.name.length === 0

      this.setState({ canGoControls: !isEmpty })
      if (isEmpty) return false
    }

    this.setState({ showControls: !this.state.showControls })
  }

  handleCancelAction () {
    const group: Group = Object.assign(this.state.currentGroup, { name: this.memoizedGroupName })

    this.setState({ currentGroup: group })
    this.props.onToggleModal()
  }

  async loadPermissions () {
    this.setState({ loadingPermissions: true })
    try {
      const { currentGroup } = this.state
      let {
        listGroupPermissions,
        listUserGroups,
        getUserGroup
      }: any = await permissionService.listGroupPermissionsAndGroups(currentGroup.id)
      this.setState({
        permissions: listGroupPermissions,
        groups: listUserGroups,
        loadingPermissions: false,
        permissionsLoaded: true,
        currentPermissions: getUserGroup || null
      })
    } catch (e) {
      console.error(e)
      this.setState({ loadingPermissions: false, permissionsLoaded: false })
      NotificationComponent.Notify('error', I18n.l("permissions.form.loadPermissions.error"), e);
    }
  }

  async savePermissions () {
    const action = this.props.currentGroup.id ? 'updateUserGroup' : 'createUserGroup'
    this.setState({ savingPermissions: true })
    try {
      await permissionService[action](this.state.finalPermissions)
      this.setState({ savingPermissions: false })
      this.props.onToggleModal()
      this.props.onLoadGroups()
      NotificationComponent.Notify('success', I18n.l("common.success"), I18n.l("permissions.form.create.success"))
    } catch (e) {
      console.error(e)
      this.setState({ savingPermissions: false })
      NotificationComponent.Notify('error', I18n.l("permissions.form.create.error"), e);
    }
  }

  render() {
    const { showControls, permissions, groups, currentGroup, canGoControls, loadingPermissions, permissionsLoaded, finalPermissions, currentPermissions, gridKey } = this.state
    
    return (
      <Modal
        visible={this.props.visible}
        title={I18n.l("permissions.createGroupModal.createGroup")}
        footer={false}
        closable={false}
        width={'880px'}
        destroyOnClose={true}
        className="generate-group"
      >
        <ModalStyle>
          <PermissionsTopImage showControls={showControls} />

          {!showControls && <PermissionsDefineGroup
            onDefineGroupName={this.handleDefineGroupName}
            onToggleModal={this.handleCancelAction}
            onToggleControls={this.handleToggleControls}
            currentGroup={currentGroup}
            canGoControls={canGoControls}
          />}

          {showControls && <PermissionsControls 
            permissions={permissions} 
            groups={groups}
            onChangePermission={this.handleChangePermission}
            onToggleModal={this.handleCancelAction}
            onToggleControls={this.handleToggleControls}
            onCreateInitialPermissionsObject={this.createInitialPermissionsObject}
            loadPermissions={this.loadPermissions}
            currentGroup={currentGroup}
            loadingPermissions={loadingPermissions}
            permissionsLoaded={permissionsLoaded}
            finalPermissions={finalPermissions}
            savePermissions={this.savePermissions}
            currentPermissions={currentPermissions}
            getCurrentPermissionValue={this.getCurrentPermissionValue}
            onChangeAllPermissions={this.handleChangeAllPermissions}
            gridKey={gridKey}
          />}
        </ModalStyle>
      </Modal>
    )
  }
}

export default PermissionsModal